import { useState, useCallback, useEffect, useRef } from 'react';
import { Outlet, useLocation, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from './Sidebar';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';
import { FeedbackModal } from '@/components/feedback/FeedbackModal';
import { useFeedbackPrompt } from '@/hooks/useFeedbackPrompt';
import { isSubmitted, snooze } from '@/components/feedback/feedbackStorage';
import { cn } from '@/lib/utils';

export interface AppLayoutContext {
  setNoPadding: (value: boolean) => void;
}

export function useAppLayout() {
  return useOutletContext<AppLayoutContext>();
}

export default function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [noPadding, setNoPadding] = useState(false);
  const location = useLocation();
  const { t } = useTranslation('common');

  // Feedback modal: opened from the sidebar link or proactively after engagement
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackSource, setFeedbackSource] = useState<'sidebar' | 'proactive'>('sidebar');
  const proactiveShownRef = useRef(false);
  const feedbackEligible = useFeedbackPrompt();

  const handleSetNoPadding = useCallback((value: boolean) => {
    setNoPadding(value);
  }, []);

  const openFeedbackFromSidebar = useCallback(() => {
    setFeedbackSource('sidebar');
    setFeedbackOpen(true);
    setSidebarOpen(false);
  }, []);

  const handleFeedbackOpenChange = useCallback(
    (next: boolean) => {
      setFeedbackOpen(next);
      // If a proactive prompt is dismissed without submitting, snooze it
      if (!next && feedbackSource === 'proactive' && !isSubmitted()) {
        snooze();
      }
    },
    [feedbackSource],
  );

  // Proactively surface the modal once per session, on the dashboard, after a short delay
  const onDashboard =
    location.pathname === '/dashboard' || location.pathname === '/en/dashboard';
  useEffect(() => {
    if (!feedbackEligible || proactiveShownRef.current || !onDashboard) return;
    const timer = setTimeout(() => {
      proactiveShownRef.current = true;
      setFeedbackSource('proactive');
      setFeedbackOpen(true);
    }, 2500);
    return () => clearTimeout(timer);
  }, [feedbackEligible, onDashboard]);

  return (
    <div className="flex h-screen overflow-hidden bg-[#F8F8F8]">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onOpenFeedback={openFeedbackFromSidebar}
      />

      <main className={cn("flex-1 overflow-auto", noPadding ? "" : "p-[16px] md:pt-[40px] md:px-[40px] md:pb-[40px]")}>
        {/* Mobile hamburger button */}
        <button
          onClick={() => setSidebarOpen(true)}
          className="mb-[16px] p-[8px] rounded-[8px] hover:bg-[var(--grey-accent,#eeeeee)] transition-colors md:hidden"
          aria-label={t('appLayout.openMenu')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 7H21" stroke="#0d0d0d" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M3 12H21" stroke="#0d0d0d" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M3 17H21" stroke="#0d0d0d" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>

        <ErrorBoundary key={location.pathname}>
          <Outlet context={{ setNoPadding: handleSetNoPadding } satisfies AppLayoutContext} />
        </ErrorBoundary>
      </main>

      <FeedbackModal
        open={feedbackOpen}
        onOpenChange={handleFeedbackOpenChange}
        source={feedbackSource}
      />
    </div>
  );
}
